import {
  Button,
  Form,
  InputNumber,
  Spin,
  Switch,
  Typography,
  Select,
  message,
  Tooltip,
} from 'antd';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import {
  AppStore,
  CarStore,
  CurrentUserStore,
  ShopStore,
  CarStatusStore,
} from 'stores'; // Ensure CarStore has the required state
import { Logger } from 'stores/Classes';
import styled from 'styled-components';
import {
  UpOutlined,
  DownOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { customFleets } from 'shared';
const { Text } = Typography;
const { Option } = Select;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 25px;
`;

const SelectWrapper = styled.div`
  position: relative;

  .ant-select-selector {
    transition: all 0.3s ease;
    border-radius: 8px !important;
    height: 40px !important;
    display: flex;
    align-items: center;
    box-shadow: none !important;
    border-width: 2px !important;
    border-style: solid !important;
    border-color: ${({ selectedcolor }) =>
      selectedcolor || '#d9d9d9'} !important;
  }

  .ant-select-focused .ant-select-selector,
  .ant-select-open .ant-select-selector,
  .ant-select-selector:hover {
    border-color: ${({ selectedcolor }) =>
      selectedcolor || '#bfbfbf'} !important;
  }

  .ant-select-arrow {
    transition: all 0.3s ease;
  }
`;

class EditProfilePage extends Component {
  formRef = React.createRef();

  state = {
    shopId: 0,
    loadingInitial: true,
    shopStores: [],
    markAsCompleteThresholdHours: 5,
    saving: false,
    taxType: 'absolute',
    taxValue: 0,
    taxOptions: [],
    demoDataEnabled: CarStore.demo || false, // Initialize from CarStore
    expandIssues: true,
    expandDvirs: true,

    availableStatuses: [],
    safeStatusSelected: '',
    unsafeStatusSelected: '',
    safeStatusColor: '',
    unsafeStatusColor: '',
  };

  async componentDidMount() {
    this.load(this.shopId);
    const { shop = [] } = CurrentUserStore.user;

    try {
      const settings = await ShopStore.getShopSettings(
        ShopStore.currentShop.id
      );
      const fleetSettings = _.get(
        settings.result,
        '[0].settings.fleetsettings',
        {}
      );

      let demoDataEnabled = _.get(
        fleetSettings,
        'demoDataEnabled',
        CarStore.demo
      );

      // ✅ If user is admin OR real cars exist, force toggle OFF
      if (CurrentUserStore.user.role === 'admin' || CarStore.hasRealCars) {
        console.log('🚫 Forcing demo toggle OFF (Admin or Real Cars Exist)');
        demoDataEnabled = false;
      } else {
        // ✅ Otherwise, ensure it matches hasDemoCars
        demoDataEnabled = CarStore.hasDemoCars;
      }
      this.manageDVIRs();
      this.setState({
        shopId: ShopStore.currentShop.id,
        shopStores: shop.map((item) => ({
          label: item.name,
          value: item.id,
        })),
        demoDataEnabled,
      });

      // ✅ Update CarStore state
      CarStore.demo = demoDataEnabled;
    } catch (err) {
      console.error('Error fetching demoDataEnabled state:', err);
    }
  }

  hasDVIRChanged = () => {
    const {
      safeStatusSelected,
      unsafeStatusSelected,
      initialSafeStatusSelected,
      initialUnsafeStatusSelected,
    } = this.state;

    return (
      safeStatusSelected !== initialSafeStatusSelected ||
      unsafeStatusSelected !== initialUnsafeStatusSelected
    );
  };

  handleSelectChange = (value, type) => {
    const selectedStatus = this.state.availableStatuses.find(
      (status) => status.id.toString() === value
    );
    this.setState({
      [`${type}StatusSelected`]: selectedStatus?.id.toString(),
      [`${type}StatusColor`]: selectedStatus?.color || '',
    });
  };

  cancelDVIRSettings = () => {
    console.log('🔁 Resetting to initial values:', {
      safe: this.state.initialSafeStatusSelected,
      unsafe: this.state.initialUnsafeStatusSelected,
    });

    const safeColor = this.getStatusColorById(
      this.state.initialSafeStatusSelected?.toString()
    );
    const unsafeColor = this.getStatusColorById(
      this.state.initialUnsafeStatusSelected?.toString()
    );

    this.setState(
      {
        safeStatusSelected: this.state.initialSafeStatusSelected?.toString(),
        unsafeStatusSelected:
          this.state.initialUnsafeStatusSelected?.toString(),
        safeStatusColor: safeColor,
        unsafeStatusColor: unsafeColor,
      },
      () => {
        console.log('✅ State after Cancel reset:', {
          safeStatusSelected: this.state.safeStatusSelected,
          unsafeStatusSelected: this.state.unsafeStatusSelected,
          safeStatusColor: this.state.safeStatusColor,
          unsafeStatusColor: this.state.unsafeStatusColor,
        });
      }
    );
  };

  resetDVIRSettings = () => {
    this.setState(
      {
        safeStatusSelected: '',
        unsafeStatusSelected: '',
        safeStatusColor: '',
        unsafeStatusColor: '',
      },
      () => {
        /*console.log('🔁 Reset to empty. State:', {
        safeStatusSelected: this.state.safeStatusSelected,
        unsafeStatusSelected: this.state.unsafeStatusSelected
      });*/
      }
    );
  };

  getStatusColorById = (id) => {
    const color = this.state.availableStatuses.find(
      (s) => s.id.toString() === id
    )?.color;
    // console.log(`🎨 getStatusColorById(${id}) →`, color);
    return color || '';
  };

  handleSaveDVIRSettings = async () => {
    const { safeStatusSelected, unsafeStatusSelected, availableStatuses } =
      this.state;

    const safeStatus = availableStatuses.find(
      (s) => s.id.toString() === safeStatusSelected
    );
    const unsafeStatus = availableStatuses.find(
      (s) => s.id.toString() === unsafeStatusSelected
    );

    const payload = [
      {
        dvir_status: 'SAFE/RESOLVED',
        key: safeStatus?.key || 'none1',
        description: safeStatus?.description || 'None',
        is_active: !!safeStatusSelected,
      },
      {
        dvir_status: 'UNSAFE',
        key: unsafeStatus?.key || 'none2',
        description: unsafeStatus?.description || 'None',
        is_active: !!unsafeStatusSelected,
      },
    ];

    // console.log('🚀 Saving DVIR Payload:', payload);

    // Check for duplicate keys in the payload
    const keys = payload.map((item) => item.key);
    const hasDuplicateKeys = keys.some(
      (key, index) => keys.indexOf(key) !== index
    );

    if (hasDuplicateKeys) {
      // Show error message if duplicate keys are found
      message.error(
        'Error: Status keys cannot be the same for SAFE/RESOLVED and UNSAFE.'
      );
      return; // Exit the function early
    }

    // Set loading state
    this.setState({ savingForm: true });

    try {
      const result = await CarStore.postDvirAvailabilityMapping(
        ShopStore.currentShop.id,
        payload
      );
      // console.log(result);

      // Show success message
      message.success('DVIR settings saved successfully!');
    } catch (error) {
      // console.error('❌ Error saving DVIR settings:', error);

      // Show error message
      message.error('Failed to save DVIR settings. Please try again.');
    } finally {
      // Reset loading state
      this.setState({ savingForm: false });
    }
  };

  loadShopStatuses = async () => {
    try {
      const statuses = await CarStatusStore.fetchShopCarAvailblityStatuses(
        ShopStore.currentShop.id
      );
      if (statuses && statuses.length) {
        this.setState({ availableStatuses: statuses });
      }
    } catch (error) {
      console.error('Error fetching car availability statuses', error);
    }
  };

  toggleExpandIssues = () => {
    this.setState({ expandIssues: !this.state.expandIssues });
  };

  toggleExpandDVIR = () => {
    this.setState({ expandDvirs: !this.state.expandDvirs });
  };

  manageDVIRs = async () => {
    // Fetch DVIR mapping and statuses in parallel
    const getParentFleetId = CarStore.getParentFleetId(
      ShopStore.currentShop.id
    );
    const [dvirMapping, allStatuses] = await Promise.all([
      CarStore.getDvirAvailabilityMapping(ShopStore.currentShop.id),
      CarStatusStore.fetchShopCarStatuses(getParentFleetId),
    ]);

    //console.log('🔁 DVIR Mapping:', dvirMapping);
    /* console.log('📋 All Statuses:', allStatuses.map(s => ({
      id: s.id,
      key: s.key,
      description: s.description,
      color: s.color
    }))); */

    let safeStatusSelected = '';
    let unsafeStatusSelected = '';
    let safeStatusColor = '';
    let unsafeStatusColor = '';
    // console.log(`Checking dvirMapping ${Array.isArray(dvirMapping)}`);
    // console.log(`Checking allStatuses ${Array.isArray(allStatuses)}`);
    if (Array.isArray(dvirMapping) && Array.isArray(allStatuses)) {
      const safeMapping = dvirMapping.find(
        (item) => item.dvir_status === 'SAFE/RESOLVED'
      );
      const unsafeMapping = dvirMapping.find(
        (item) => item.dvir_status === 'UNSAFE'
      );

      /*console.log('🔍 SAFE Mapping to match:', safeMapping);
      console.log('🔍 UNSAFE Mapping to match:', unsafeMapping);
      console.log('🧩 Available Keys:', allStatuses.map(s => s.key));*/

      if (safeMapping) {
        const safeStatusOption = allStatuses.find(
          (status) =>
            status.key?.trim().toLowerCase() ===
            safeMapping.key?.trim().toLowerCase()
        );

        // console.log('✅ Matched SAFE Status:', safeStatusOption);

        if (safeStatusOption) {
          safeStatusSelected = safeStatusOption.id.toString();
          safeStatusColor = safeStatusOption.color;
        } else {
          // console.warn('⚠️ No matching status found for SAFE key:', safeMapping.key);
        }
      }

      if (unsafeMapping) {
        const unsafeStatusOption = allStatuses.find(
          (status) =>
            status.key?.trim().toLowerCase() ===
            unsafeMapping.key?.trim().toLowerCase()
        );

        // console.log('✅ Matched UNSAFE Status:', unsafeStatusOption);

        if (unsafeStatusOption) {
          unsafeStatusSelected = unsafeStatusOption.id.toString();
          unsafeStatusColor = unsafeStatusOption.color;
        } else {
          console.warn(
            '⚠️ No matching status found for UNSAFE key:',
            unsafeMapping.key
          );
        }
      }
    }
    this.setState(
      {
        safeStatusSelected,
        unsafeStatusSelected,
        safeStatusColor,
        unsafeStatusColor,
        initialSafeStatusSelected: safeStatusSelected,
        initialUnsafeStatusSelected: unsafeStatusSelected,
        availableStatuses: allStatuses,
        loadingInitial: false,
      },
      () => {
        /*console.log('✅ Final State after mount:', {
        safeStatusSelected: this.state.safeStatusSelected,
        unsafeStatusSelected: this.state.unsafeStatusSelected,
        safeStatusColor: this.state.safeStatusColor,
        unsafeStatusColor: this.state.unsafeStatusColor,
        availableStatuses: this.state.availableStatuses.map(s => s.key)
      });*/
      }
    );
  };

  componentDidUpdate(prevProps, prevState) {
    // ✅ Ensure demo toggle updates when the fleet changes
    if (prevState.shopId !== this.state.shopId) {
      console.log(`🏢 Switched fleet to ID: ${this.state.shopId}`);

      const fleetHasRealCars = CarStore.hasRealCars;
      let demoDataEnabled = this.state.demoDataEnabled;

      if (fleetHasRealCars) {
        console.log('🚫 New fleet has real cars. Forcing demo mode OFF.');
        demoDataEnabled = false;
      } else if (CurrentUserStore.user.role === 'admin') {
        console.log(
          '✅ Admin selected a fleet without real cars. Unlocking toggle.'
        );
        demoDataEnabled = CarStore.hasDemoCars;
      }

      this.setState({ demoDataEnabled });
    }
  }

  toggleDemoData = async () => {
    CarStore.toggleDemo();
    this.setState({ demoDataEnabled: CarStore.demo });

    if (CarStore.demo) {
      console.log('📢 Assigning Demo Cars...');
      await CarStore.assignDemoCar();
    } else {
      console.log('🚫 Removing Demo Cars...');
      await CarStore.unassignDemoCar();
      this.setState({ demoDataEnabled: false });

      if (!CarStore.hasDemoCars) {
        console.log('⚠️ No more demo cars. Preventing invalid UI state.');
        this.setState({ demoDataEnabled: false }); // Ensure the toggle updates correctly
      }
    }
  };

  validateNumber = (number) => {
    if (number < 1) {
      return {
        validateStatus: 'error',
        errorMsg: 'Number must be greater or equal to 1',
      };
    }
    return {
      validateStatus: 'success',
      errorMsg: null,
    };
  };

  updateFleetRules = async () => {
    this.setState({ saving: true });
    try {
      const { markAsCompleteThresholdHours } = this.state;
      // get the current shop settings
      const currentSettings = await ShopStore.getShopSettings(
        ShopStore.currentShop.id
      );

      const shopSettings = currentSettings?.result?.[0]?.settings || {};
      const fleetSettings = shopSettings.fleetsettings || {};

      await ShopStore.upsertShopSettings(ShopStore.currentShop.id, {
        ...shopSettings,
        fleetsettings: {
          ...fleetSettings,
          markAsCompleteThresholdHours: markAsCompleteThresholdHours,
        },
      });
      AppStore.addSuccess('Fleet rules updated successfully');
    } catch (error) {
      Logger.log(error);
      AppStore.addError('Error updating fleet rules');
    } finally {
      this.setState({ saving: false });
    }
  };

  load = async (shopId) => {
    this.setState({
      taxType: '',
      taxValue: 0,
      taxOptions: [],
    });
    try {
      const { result } = await ShopStore.getShopSettings(shopId);
      if (
        !_.isEmpty(result) &&
        !_.isNil(result[0].settings) &&
        !_.isNil(result[0].settings.fleetsettings)
      ) {
        const fleetSettings = result[0].settings.fleetsettings;

        const value = _.get(fleetSettings, 'markAsCompleteThresholdHours', 5);

        this.setState(
          {
            markAsCompleteThresholdHours: value,
            taxType: fleetSettings.taxType,
            taxValue: fleetSettings.taxValue,
            taxOptions: fleetSettings.taxOptions,
          },
          () => {
            this.setState({ loadingInitial: false });
          }
        );
      }
    } catch (e) {
      console.log('error', e);
    } finally {
      this.setState({ loadingInitial: false });
    }
  };

  renderSelectField = (type) => {
    const selectedValue = this.state[`${type}StatusSelected`];
    const selectedColor = this.state[`${type}StatusColor`];

    console.log(`Rendering ${type} dropdown:`, {
      selectedValue,
      selectedColor,
    });

    return (
      <SelectWrapper selectedcolor={selectedValue ? selectedColor : null}>
        <Select
          placeholder="None selected"
          value={selectedValue || undefined}
          style={{
            width: '195px',
            color: selectedColor || '#000',
            fontWeight: 600,
          }}
          onChange={(value) => this.handleSelectChange(value, type)}
          dropdownStyle={{ backgroundColor: '#ffffff' }}
          optionLabelProp="label"
          suffixIcon={
            <DownOutlined style={{ color: selectedColor || '#000' }} />
          }
        >
          {(this.state.availableStatuses || []).map((status) => (
            <Option
              key={status.id}
              value={status.id.toString()}
              label={
                <div
                  style={{
                    color: status.color,
                    fontWeight: 600,
                    textAlign: 'center',
                  }}
                >
                  {status.description}
                </div>
              }
            >
              <div
                style={{
                  padding: '4px 8px',
                  border: `1px solid ${status.color}`,
                  borderRadius: '6px',
                  color: status.color,
                  fontWeight: 600,
                  textAlign: 'center',
                }}
              >
                {status.description}
              </div>
            </Option>
          ))}
        </Select>
      </SelectWrapper>
    );
  };

  render() {
    const isSummitFleet = customFleets.summitMaterialsAll.includes(
      ShopStore.currentShop.id
    );
    return this.state.loadingInitial ? (
      <LoadingContainer>
        <Spin />
      </LoadingContainer>
    ) : (
      <React.Fragment>
        {/* 🔵 Issues Section */}
        <div
          onClick={this.toggleExpandIssues}
          style={{
            display: 'flex',
            width: '60%',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            paddingBottom: '8px',
          }}
        >
          <Text style={{ color: '#1890ff', fontWeight: 500 }}>Issues</Text>
          {this.state.expandIssues ? <UpOutlined /> : <DownOutlined />}
        </div>

        {this.state.expandIssues && (
          <Form
            ref={this.formRef}
            style={{ width: '40%' }}
            onFinish={this.updateFleetRules}
            className="edit-fleet-rules-form"
          >
            <Form.Item
              label="Clear Fault Codes After"
              name="markAsCompleteThresholdHours"
              initialValue={this.state.markAsCompleteThresholdHours}
            >
              <>
                <InputNumber
                  placeholder="5"
                  value={this.state.markAsCompleteThresholdHours}
                  onChange={(value) =>
                    this.setState({
                      markAsCompleteThresholdHours: value,
                    })
                  }
                />
                <Text style={{ marginLeft: '3px' }} type="secondary">
                  Driving Hours
                </Text>
              </>
            </Form.Item>

            {/* ✅ Toggle Sample Data Switch with working UI update */}
            <Form.Item label="Turn on Demo Fleet Data">
              <Switch
                checkedChildren="On"
                unCheckedChildren="Off"
                checked={this.state.demoDataEnabled}
                onChange={this.toggleDemoData}
                disabled={
                  CarStore.hasRealCars ||
                  (CurrentUserStore.user.role === 'admin' &&
                    CarStore.hasRealCars)
                }
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="login-form-button"
                disabled={!this.state.markAsCompleteThresholdHours}
                loading={this.state.saving}
              >
                Update Rules
              </Button>
            </Form.Item>
          </Form>
        )}
        {/* DVIR Section */}
        {isSummitFleet && (
          <div>
            <div
              onClick={this.toggleExpandDVIR}
              style={{
                display: 'flex',
                width: '60%',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                paddingBottom: '8px',
                marginTop: '24px',
              }}
            >
              <Text style={{ color: '#1890ff', fontWeight: 500 }}>DVIR</Text>
              {this.state.expandDvirs ? <UpOutlined /> : <DownOutlined />}
            </div>
            {this.state.expandDvirs && (
              <Form
                style={{ width: '40%', marginTop: '16px' }}
                layout="vertical"
              >
                <Text style={{ marginBottom: '8px', display: 'inline-block' }}>
                  Allow DVIRs to change the status of the asset:
                  <Tooltip
                    title="Link the DVIR status of an asset to automatically update the status for asset availability, e.g., DVIRs marked 'Safe' will update the asset to Running, 'Unsafe' updates it to Down."
                    trigger="hover"
                  >
                    <InfoCircleOutlined
                      style={{
                        cursor: 'pointer',
                        marginLeft: '8px',
                        color: '#1890ff',
                      }}
                    />
                  </Tooltip>
                </Text>

                {/* Table layout with 3 columns */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '180px 80px 1fr',
                    rowGap: '16px',
                    columnGap: '16px',
                    alignItems: 'center',
                    marginTop: '16px',
                  }}
                >
                  {/* Header Row */}
                  <div style={{ fontWeight: 600 }}>DVIR Status</div>
                  <div></div>
                  <div style={{ fontWeight: 600 }}>
                    Asset Availability Status
                  </div>

                  {/* SAFE/RESOLVED Row */}
                  <div
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#52c41a',
                      fontWeight: 600,
                      padding: '6px 16px',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '40px',
                    }}
                  >
                    SAFE/RESOLVED
                  </div>
                  <div style={{ textAlign: 'right' }}>Link to →</div>
                  {this.renderSelectField('safe')}

                  {/* UNSAFE Row */}
                  <div
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#f5222d',
                      fontWeight: 600,
                      padding: '6px 16px',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '40px',
                    }}
                  >
                    UNSAFE
                  </div>
                  <div style={{ textAlign: 'right' }}>Link to →</div>
                  {this.renderSelectField('unsafe')}
                </div>

                <div
                  style={{
                    marginTop: '24px',
                    display: 'grid',
                    gridTemplateColumns: '180px 80px 1fr',
                    alignItems: 'center',
                    columnGap: '32px',
                    width: '186px', // Ensure this matches DVIR section width
                    maxWidth: '100%',
                  }}
                >
                  {/* Reset (left aligned) */}
                  <div style={{ textAlign: 'left' }}>
                    <Button
                      type="link"
                      style={{ padding: 0 }}
                      onClick={this.resetDVIRSettings}
                    >
                      Reset
                    </Button>
                  </div>

                  {/* Cancel (center aligned) */}
                  <div style={{ textAlign: 'center' }}>
                    <Button
                      disabled={!this.hasDVIRChanged()}
                      onClick={this.cancelDVIRSettings}
                    >
                      Cancel
                    </Button>
                  </div>

                  {/* Save (right aligned, stays within layout) */}
                  <div style={{ textAlign: 'right' }}>
                    <Button
                      type="primary"
                      style={{ fontWeight: 600 }}
                      onClick={this.handleSaveDVIRSettings}
                    >
                      Save DVIR Settings
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </div>
        )}
      </React.Fragment>
    );
  }
}

EditProfilePage.propTypes = {
  form: PropTypes.object.isRequired,
};

export default EditProfilePage;
